import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MuiCheckbox from "./MuiCheckbox";
import { Helmet } from "react-helmet-async";
import { productOrderedHistory } from "../../api/Order";

const PAGE_SIZE = 10;

interface Product {
    id: string;
    name: string;
    img: string;
    seller: string;
    maxQty: number;
    checked: boolean;
    qty: number;
    price: string;
    purchaseDate?: string;
}

export default function HistoryPurchasedList() {
    const [products, setProducts] = useState<Product[]>([]);
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);

    useEffect(() => {
        let isMounted = true;

        const loadPurchasedProducts = async () => {
            const res = await productOrderedHistory(page, PAGE_SIZE);

            if (!isMounted) {
                return;
            }

            if (!res?.status || !res.data) {
                setProducts([]);
                setTotalPage(1);
                return;
            }

            const mappedProducts: Product[] = (res.data.items || []).map((item) => ({
                id: String(item.id),
                name: item.name || "",
                img: item.avatar || "",
                seller: "Nhà cung cấp",
                maxQty: Number(item.purchased_quantity || 100),
                checked: false,
                qty: 0,
                price: String(item.price || 0),
            }));

            setProducts(mappedProducts);
            setTotalPage(Math.max(1, Math.ceil(Number(res.data.total || 0) / PAGE_SIZE)));
        };

        loadPurchasedProducts();

        return () => {
            isMounted = false;
        };
    }, [page]);

    const totalChecked = products.filter(p => p.checked).length;
    const allChecked = totalChecked === products.length && products.length > 0;
    const indeterminate = totalChecked > 0 && totalChecked < products.length;

    const toggleCheckItem = (id: string) => {
        setProducts(prev =>
            prev.map(p =>
                p.id === id ? { ...p, checked: !p.checked } : p
            )
        );
    };

    const toggleCheckAll = () => {
        const target = !(allChecked && !indeterminate);
        setProducts(prev => prev.map(p => ({ ...p, checked: target })));
    };

    const deleteSelected = () => {
        setProducts(prev => prev.filter(p => !p.checked));
    };

    const changeQty = (id: string, value: number) => {
        setProducts((prev) =>
            prev.map((p) =>
                p.id === id
                    ? { ...p, qty: Math.max(0, Math.min(value, p.maxQty)) }
                    : p
            )
        );
    };

    const goPrev = () => {
        if (page > 1) setPage(page - 1)
    }

    const goNext = () => {
        if (page < totalPage) setPage(page + 1)
    }

    return (
        <>
            <Helmet>
                <title>Sản phẩm đã mua - Dược Tâm Hạnh</title>
                <meta name="description" content="Danh sách sản phẩm đã mua trên Dược Tâm Hạnh" />
            </Helmet>
            <div className="MuiGrid-root MuiGrid-container MuiGrid-direction-xs-column">
                <header className="MuiPaper-root MuiAppBar-root MuiAppBar-positionFixed MuiAppBar-colorPrimary styles_appBar__Kzodx mui-fixed MuiPaper-elevation4 w-full-1199-24px show-1199">
                    <div className="MuiToolbar-root MuiToolbar-regular styles_toolBar__DQTee MuiToolbar-gutters">
                        <Link to="/" className="MuiButtonBase-root MuiButton-root MuiButton-text styles_btnHamburger__POqzB" aria-label="back pagehome">
                            <span className="MuiButton-label">
                                <span style={{ boxSizing: "border-box", display: "inline-block", overflow: "hidden", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", position: "relative", maxWidth: "100%" }}>
                                    <span style={{ boxSizing: "border-box", display: "block", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", maxWidth: "100%" }}>
                                        <img alt="" aria-hidden="true" src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2724%27%20height=%2724%27/%3e" style={{ display: "block", maxWidth: "100%", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px" }} />
                                    </span>
                                    <img src="https://cdn-web-mobile-next.thuocsi.vn/images/header-v2/arrow-left.svg" decoding="async" data-nimg="intrinsic" style={{ position: "absolute", inset: "0px", boxSizing: "border-box", padding: "0px", border: "none", margin: "auto", display: "block", width: "0px", height: "0px", minWidth: "100%", maxWidth: "100%", minHeight: "100%", maxHeight: "100%" }} />
                                </span>
                            </span>
                            <span className="MuiTouchRipple-root"></span>
                        </Link>
                        <p className="MuiTypography-root styles_formTitle__o8AZx MuiTypography-body1">Sản phẩm đã mua</p>
                    </div>
                </header>
                <div className="styles_searchWrapper__1VsnB show-1199">
                    <div className="MuiInputBase-root MuiInput-root styles_root_input__fUHE5 MuiInputBase-adornedStart">
                        <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1a5basm" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="SearchIcon">
                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                        </svg>
                        <input placeholder="Nhập tên thuốc, hoạt chất cần tìm" type="search" className="MuiInputBase-input MuiInput-input styles_input__p8e0Z MuiInputBase-inputAdornedStart MuiInputBase-inputTypeSearch MuiInput-inputTypeSearch" value="" />
                    </div>
                </div>
                <div className="style_wrapperContent__9ddkL">

                    {/* ==== TOP BAR ==== */}
                    <div className="style_containerTop__j5JB3">
                        <span className="style_titleContent__NuXwE">
                            Danh sách sản phẩm đã mua
                        </span>
                    </div>

                    {/* ==== PRODUCT LIST ==== */}
                    {products.map((p) => (
                        <div key={p.id} className="style_wrapperCard__yyX_D">

                            <div className="styles_cardContainer__roWvH style_cardItem__rKGSb !pr-0">
                                <div className="styles_flex__YOT7Y">

                                    {/* image */}
                                    <a href="#">
                                        <div className="styles_wrapperImage__vkqUl">
                                            <img src={p.img} alt={p.name} />
                                        </div>
                                    </a>

                                    {/* info */}
                                    <div className="styles_wrapperInfoProduct__zIAXY">
                                        {/* Show purchase date if available */}
                                        {p.purchaseDate && (
                                            <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                                                Ngày mua: {new Date(p.purchaseDate).toLocaleDateString("vi-VN")}
                                            </div>
                                        )}
                                        <span className="styles_name__JFs3F">{p.name}</span>
                                        <div className="styles_sellerContainer__zCB_8">
                                            <span style={{ boxSizing: "border-box", display: "inline-block", overflow: "hidden", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", position: "relative", maxWidth: "100%" }}>
                                                <span style={{ boxSizing: "border-box", display: "block", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", maxWidth: "100%" }}>
                                                    <img alt="" aria-hidden="true" src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2713%27%20height=%2713%27/%3e" style={{ display: "block", maxWidth: "100%", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px" }} />
                                                </span>
                                                <img src="https://cdn-web-next.thuocsi.vn/icons/seller_store.svg" decoding="async" data-nimg="intrinsic" style={{ position: "absolute", inset: "0px", boxSizing: "border-box", padding: "0px", border: "none", margin: "auto", display: "block", width: "0px", height: "0px", minWidth: "100%", maxWidth: "100%", minHeight: "100%", maxHeight: "100%" }} />
                                            </span>
                                            <a className="styles_cardNameStore__T1gCP" target="_blank" href="/flagship-store/merck-healthcare">Merck Healthcare</a>
                                        </div>
                                        <div className="styles_cardBadges__umsyP MuiBox-root css-0">
                                            <a className="styles_mobile_rootBase__8z7PQ" target="_blank" href="/deals">
                                                <img alt="tag-/deals" src="https://gcs.buymed.com/thuocsi-live/internal/cms/2024e1a6ffa3d0111cad282d9eeed6f3Tag_Label_-_Icon+Text-5.svg" title="Khuyến Mãi" />
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <div className="styles_wrapperContent__K5zzD">
                                    {/* <div className="styles_wrapperDiscount__H_zPT">
                                    <p className="MuiTypography-root styles_promo__Q5YiA MuiTypography-body1"> -0%</p>
                                    <a className="styles_cardDiscount__HubGJ" href="#">
                                        <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                                            <div className="styles_canvasPrice__vw932 text-[15px] line-through text-[#333333]" style={{ height: "25px" }}>{p.price ? Number(p.price).toLocaleString() : '0'}đ</div>
                                        </div>
                                    </a>
                                </div> */}
                                    <div>
                                        <a className="" href="#">
                                            <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                                                <div className="styles_canvasPrice__vw932 text-[22px] text-[#428c04]" style={{ height: "34px" }}>{p.price ? (p.price) : '0'}đ</div>
                                            </div>
                                        </a>
                                    </div>
                                </div>

                                {/* ==== quantity control ==== */}
                                <div className="styles_wrapperQuantityControl__LHcc9">
                                    <span className="styles_cardNoted__fnyQc">
                                        Đặt tối đa {p.maxQty} sản phẩm
                                    </span>

                                    <div className="styles_maxWidth__qS6it">
                                        <div className="styles_container___oLk_ styles_quantityControlContainer__1mAaj qty-control-viewed-wrapper">

                                            {/* minus */}
                                            <div
                                                className="styles_minusContainerNew__flGSc qty-control-viewed-minus"
                                                onClick={() => changeQty(p.id, p.qty - 1)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M7 12H17" stroke="#333333" stroke-width="2" stroke-linecap="round"></path></svg>
                                            </div>

                                            {/* input */}
                                            <div className="styles_groupInput__H1lLR qty-control-viewed-input-group">
                                                <div className="styles_cartIconContainer__FoKUV">
                                                    <div className="styles_inputVisible__m_CzR">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                            <path d="M14.3608 3.9375H4.00136L3.11622 2.35095C3.01687 2.17287 2.82892 2.0625 2.625 2.0625H1.5C1.18934 2.0625 0.9375 2.31434 0.9375 2.625C0.9375 2.93566 1.18934 3.1875 1.5 3.1875H2.2947L3.16822 4.75324L5.18278 9.21066L5.18484 9.21521L5.36228 9.60781L3.33965 11.7653C3.19422 11.9204 3.14869 12.1443 3.22199 12.3439C3.29529 12.5435 3.47488 12.6847 3.68614 12.7089L5.52974 12.9196C7.83575 13.1831 10.1643 13.1831 12.4703 12.9196L14.3139 12.7089C14.6225 12.6736 14.8442 12.3948 14.8089 12.0861C14.7736 11.7775 14.4948 11.5559 14.1861 11.5911L12.3425 11.8018C10.1214 12.0557 7.87861 12.0557 5.65749 11.8018L4.92585 11.7182L6.41038 10.1347C6.42474 10.1194 6.43808 10.1035 6.45039 10.0871L7.015 10.1605C7.8062 10.2635 8.60596 10.2837 9.40134 10.2208C11.2564 10.074 12.9758 9.19131 14.1761 7.7693L14.6097 7.25569C14.6242 7.23844 14.6377 7.22033 14.6501 7.20146L15.4581 5.97007C16.0309 5.09724 15.4048 3.9375 14.3608 3.9375Z" fill="#999999">
                                                            </path>
                                                            <path d="M4.875 13.875C4.25368 13.875 3.75 14.3787 3.75 15C3.75 15.6213 4.25368 16.125 4.875 16.125C5.49632 16.125 6 15.6213 6 15C6 14.3787 5.49632 13.875 4.875 13.875Z" fill="#999999"></path>
                                                            <path d="M12 15C12 14.3787 12.5037 13.875 13.125 13.875C13.7463 13.875 14.25 14.3787 14.25 15C14.25 15.6213 13.7463 16.125 13.125 16.125C12.5037 16.125 12 15.6213 12 15Z" fill="#999999"></path>
                                                        </svg>
                                                    </div>
                                                    <span className="styles_currentValue__b2ijJ">1</span>
                                                </div>
                                                <div className="MuiInput-root MuiInputBase-root MuiInputBase-colorPrimary styles_newCardInput__hecWu qty-control-viewed-input css-1f63zq5">
                                                    <input
                                                        type="number"
                                                        className="MuiInput-input MuiInputBase-input css-mnn31"
                                                        value={p.qty}
                                                        onChange={(e) =>
                                                            changeQty(p.id, Number(e.target.value))
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            {/* plus */}
                                            <div
                                                className="styles_addContainerNew__v_8_6"
                                                onClick={() => changeQty(p.id, p.qty + 1)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 17V7" stroke="#428c04" stroke-width="2" stroke-linecap="round"></path><path d="M7 12L17 12" stroke="#428c04" stroke-width="2" stroke-linecap="round"></path></svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="style_bottomPagging__kzl7h">
                        <div className="styles_pagiContainer__B0imj MuiBox-root css-1vwfmvr">
                            <nav
                                aria-label="pagination navigation"
                                className="MuiPagination-root jss273"
                            >
                                <ul className="MuiPagination-ul">
                                    {/* PREV */}
                                    <li>
                                        <button
                                            className={
                                                "MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page" +
                                                (page === 1 ? " Mui-disabled" : "")
                                            }
                                            tabIndex={page === 1 ? -1 : 0}
                                            type="button"
                                            disabled={page === 1}
                                            aria-label="Go to previous page"
                                            onClick={goPrev}
                                        >
                                            <svg
                                                className="MuiSvgIcon-root MuiPaginationItem-icon"
                                                focusable="false"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                            </svg>
                                        </button>
                                    </li>

                                    {/* PAGE NUMBERS */}
                                    {Array.from({ length: totalPage }).map((_, i) => {
                                        const p = i + 1
                                        return (
                                            <li key={p}>
                                                <button
                                                    className={
                                                        "MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page" +
                                                        (page === p ? " Mui-selected" : "")
                                                    }
                                                    tabIndex={0}
                                                    type="button"
                                                    aria-current={page === p ? "true" : undefined}
                                                    aria-label={`Go to page ${p}`}
                                                    onClick={() => setPage(p)}
                                                >
                                                    {p}
                                                    <span className="MuiTouchRipple-root" />
                                                </button>
                                            </li>
                                        )
                                    })}

                                    {/* ELLIPSIS */}
                                    <li>
                                        <div className="MuiPaginationItem-root MuiPaginationItem-ellipsis">
                                            …
                                        </div>
                                    </li>

                                    {/* NEXT */}
                                    <li>
                                        <button
                                            className={
                                                "MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page" +
                                                (page === totalPage ? " Mui-disabled" : "")
                                            }
                                            tabIndex={page === totalPage ? -1 : 0}
                                            type="button"
                                            disabled={page === totalPage}
                                            aria-label="Go to next page"
                                            onClick={goNext}
                                        >
                                            <svg
                                                className="MuiSvgIcon-root MuiPaginationItem-icon"
                                                focusable="false"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                            </svg>
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
